import React, { useEffect, useState, useRef } from 'react';
import { Box, useColorMode } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { MdOutlineNavigateBefore, MdOutlineNavigateNext } from 'react-icons/md';
import { FaFileInvoice } from 'react-icons/fa';
import { MdAssignmentReturn } from 'react-icons/md';
import { useUser, useGlobal } from 'contexts';

function PurchaseTabs({ handlePurchaseClick }) {
   const { colorMode } = useColorMode();
   const { user } = useUser();
   const admin = user?.isAdmin;
   const rights = user?.userRights?.find((ur) => ur?.companyId === user?.loginhistory?.companyId);
   const { company } = useGlobal();
   const isDark = colorMode === 'dark';
   const params = useParams();
   const tabListRef = useRef(null);

   const tabs = [
      ...((user?.packageData?.[0]?.purchase_order && company?.setting?.enablePurchaseChallan && (admin || rights?.purchaseorder?.view)) ? [{
         label: 'Order',
         value: 'order',
         path: 'purchase/order',
         parentFor: ['purchase/order', 'purchase/order/daybook/:id'],
         icon: <FaFileInvoice size={18} />,
      },] : []),
      ...((user?.packageData?.[0]?.purchase_challan && company?.setting?.enablePurchaseOrder && (admin || rights?.purchasechallan?.view)) ? [{
         label: 'Challan',
         value: 'challan',
         path: 'purchase/challan',
         parentFor: ['purchase/challan', 'purchase/challan/daybook/:id'],
         icon: <FaFileInvoice size={18} />,
      },] : []),
      ...(user?.packageData?.[0]?.purchase_invoice && (admin || rights?.purchaseinvoice?.view) ? [{
         label: 'Invoice',
         value: 'invoice',
         path: 'purchase/invoice',
         parentFor: ['purchase/invoice', 'purchase/invoice/daybook/:id'],
         icon: <FaFileInvoice size={18} />,
      },] : []),
      ...(user?.packageData?.[0]?.purchase_return && (admin || rights?.purchasereturn?.view) ? [{
         label: 'Return',
         value: 'return',
         path: 'purchase/return',
         parentFor: ['purchase/return', 'purchase/return/daybook/:id'],
         icon: <MdAssignmentReturn size={20} />,
      },] : []),
   ];

   const getCurrentTabIndex = () => {
      const currentPath = params['*']?.split('/')[1] || 'invoice';
      for (let i = 0; i < tabs.length; i++) {
         if (currentPath === tabs[i].value) {
            return i;
         }
      }
      return 0;
   };

   const [activeTabIndex, setActiveTabIndex] = useState(getCurrentTabIndex());

   useEffect(() => {
      setActiveTabIndex(getCurrentTabIndex());
   }, [params['*']]);

   useEffect(() => {
      const handleKeyDown = (e) => {
         const activeElement = document.activeElement;
         const isTyping =
            activeElement?.tagName === 'INPUT' ||
            activeElement?.tagName === 'TEXTAREA' ||
            activeElement?.getAttribute('role') === 'textbox' ||
            activeElement?.isContentEditable;

         if (isTyping) return;

         if (!e.shiftKey && !e.ctrlKey && !e.altKey) {
            const key = e.key.toLowerCase();

            if (key === 'i') {
               handlePurchaseClick('invoice');
            } else if (key === 'r') {
               handlePurchaseClick('return');
            }
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, []);

   const currentPath = params['*']?.split('/')[1] || 'invoice';
   const prevTab = activeTabIndex > 0 ? tabs[activeTabIndex - 1] : null;
   const currentTab = tabs[activeTabIndex];
   const nextTab = activeTabIndex < tabs.length - 1 ? tabs[activeTabIndex + 1] : null;

   const getVisibleTabs = (count) => {
      const tabsOnEachSide = Math.floor(count / 2);
      let startIdx = Math.max(0, activeTabIndex - tabsOnEachSide);
      let endIdx = startIdx + count - 1;
      if (endIdx >= tabs.length) {
         endIdx = tabs.length - 1;
         startIdx = Math.max(0, endIdx - count + 1);
      }
      return tabs.slice(startIdx, endIdx + 1);
   };

   const NavButton = ({ disabled, onClick, children }) => (
      <button
         onClick={onClick}
         disabled={disabled}
         className={`p-2 rounded-full transition-all duration-300 ${disabled
            ? 'opacity-30 cursor-not-allowed'
            : `cursor-pointer hover:scale-110 ${isDark
               ? 'hover:bg-slate-800 text-slate-300 hover:text-white'
               : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
            }`
            }`}>
         {children}
      </button>
   );

   const TabButton = ({ tab, isActive, onClick }) => (
      <button
         onClick={() => onClick(tab.value)}
         className={`flex items-center justify-center gap-2 px-6 py-2 rounded-xl font-semibold text-sm transition-all duration-300 ${isActive
            ? `${isDark
               ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white shadow-lg ring-2 ring-purple-400/50'
               : 'bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500 text-white shadow-lg ring-2 ring-emerald-300/50'
            } scale-110`
            : `${isDark
               ? 'text-gray-400 hover:text-white hover:bg-slate-800/70'
               : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/70'
            } hover:scale-105`
            }`}>
         <div className={`flex items-center justify-center transition-transform ${isActive ? 'scale-125' : ''}`}>
            {tab.icon}
         </div>
         <span className="whitespace-nowrap">{tab.label}</span>
      </button>
   );

   return (
      <Box className="pt-14">
         <div
            className={`mb-3 rounded-2xl p-2.5 px-4 overflow-hidden ${isDark
               ? 'bg-slate-900/60 backdrop-blur-lg border border-slate-800/80'
               : 'bg-white border border-gray-200 shadow-xl shadow-gray-100/40'
               }`}>
            {/* Large screens - all tabs */}
            <div className="xl:flex items-center gap-3" ref={tabListRef}>
               {tabs.map((tab) => (
                  <TabButton
                     key={tab.value}
                     tab={tab}
                     isActive={currentPath === tab.value}
                     onClick={handlePurchaseClick}
                  />
               ))}
            </div>

            {/* Desktop - show all tabs with navigation */}
            <div className="hidden lg:flex xl:hidden items-center justify-between gap-2">
               <NavButton
                  disabled={activeTabIndex === 0}
                  onClick={() => handlePurchaseClick(tabs[activeTabIndex - 1].value)}>
                  <MdOutlineNavigateBefore size={24} />
               </NavButton>
               <div className="flex items-center gap-1 flex-1 justify-center">
                  {getVisibleTabs(7).map((tab) => (
                     <TabButton
                        key={tab.value}
                        tab={tab}
                        isActive={tab.value === currentTab.value}
                        onClick={handlePurchaseClick}
                     />
                  ))}
               </div>
               <NavButton
                  disabled={activeTabIndex === tabs.length - 1}
                  onClick={() => handlePurchaseClick(tabs[activeTabIndex + 1].value)}>
                  <MdOutlineNavigateNext size={24} />
               </NavButton>
            </div>

            {/* Tablet - show all tabs with navigation */}
            <div className="hidden md:flex lg:hidden items-center justify-between gap-2">
               <NavButton
                  disabled={activeTabIndex === 0}
                  onClick={() => handlePurchaseClick(tabs[activeTabIndex - 1].value)}>
                  <MdOutlineNavigateBefore size={24} />
               </NavButton>
               <div className="flex items-center gap-1 flex-1 justify-center">
                  {getVisibleTabs(5).map((tab) => (
                     <TabButton
                        key={tab.value}
                        tab={tab}
                        isActive={tab.value === currentTab.value}
                        onClick={handlePurchaseClick}
                     />
                  ))}
               </div>
               <NavButton
                  disabled={activeTabIndex === tabs.length - 1}
                  onClick={() => handlePurchaseClick(tabs[activeTabIndex + 1].value)}>
                  <MdOutlineNavigateNext size={24} />
               </NavButton>
            </div>

            {/* Mobile - single tab with prev/next navigation */}
            <div className="flex md:hidden items-center justify-between gap-2">
               {prevTab ? (
                  <NavButton onClick={() => handlePurchaseClick(prevTab.value)}>
                     <MdOutlineNavigateBefore size={22} />
                  </NavButton>
               ) : (
                  <div className="w-8" />
               )}
               <div
                  className={`flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-sm shadow-xl ${isDark
                     ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white ring-2 ring-purple-400/50'
                     : 'bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500 text-white ring-2 ring-emerald-300/50'
                     }`}>
                  {currentTab?.icon}
                  <span className="truncate">{currentTab?.label}</span>
               </div>
               {nextTab ? (
                  <NavButton onClick={() => handlePurchaseClick(nextTab.value)}>
                     <MdOutlineNavigateNext size={22} />
                  </NavButton>
               ) : (
                  <div className="w-8" />
               )}
            </div>
         </div>
      </Box>
   );
}

export default PurchaseTabs;
